export const ORGANIZATION_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': 'https://www.aainomatic.com/#organization',
  name: 'A&A Inomatic, LLC',
  url: 'https://www.aainomatic.com/',
} as const;

export function publicSiteOrigin(): string {
  if (process.env.REACT_APP_ENV === 'production') return 'https://booking.aacore.net';
  if (process.env.REACT_APP_ENV === 'qa') return 'https://qa.booking.aacore.net';
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return 'https://booking.aacore.net';
}

/** Public pages use a trailing slash because that is the URL the host returns with 200. */
export function publicPath(path: string): string {
  const [pathname, search = ''] = path.split('?');
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const withSlash = normalized === '/' || normalized.endsWith('/') ? normalized : `${normalized}/`;
  return search ? `${withSlash}?${search}` : withSlash;
}

export function publicAbsoluteUrl(path: string): string {
  return `${publicSiteOrigin()}${publicPath(path)}`;
}

export const HOME_SEO = {
  title: 'AACore Booking | Find and book local services',
  description:
    'Discover local service businesses and book appointments with AACore Booking. Use AI chat or online booking, then manage quotes, invoices, and payments.',
  path: '/',
};

export const FEATURES_SEO = {
  title: 'Online Booking Software for Service Businesses | AACore Booking',
  description:
    'Online booking software for service businesses. Take direct bookings, on-site appointments, recurring visits, and quotes, then send invoices and collect payments.',
  path: '/features',
};

export const SOFTWARE_APPLICATION_ID = 'https://booking.aacore.net/#software';

/** Title and description for a public business page after the API returns that business. */
export function publicTenantMetaDescription(name: string, description?: string | null): string {
  const clean = String(description || '').replace(/\s+/g, ' ').trim();
  const fallback = `Book appointments with ${name} on AACore Booking.`;
  const text = clean || fallback;
  if (text.length <= 160) return text;
  const sliced = text.slice(0, 157);
  const lastSpace = sliced.lastIndexOf(' ');
  const base = (lastSpace > 80 ? sliced.slice(0, lastSpace) : sliced).trim();
  return `${base}...`;
}

export function publicTenantSeo(input: { name: string; shortName: string; description?: string | null }): {
  title: string;
  description: string;
  path: string;
  jsonLd: Record<string, unknown>;
} {
  const name = String(input.name || '').trim() || input.shortName;
  const description = publicTenantMetaDescription(name, input.description);
  const path = `/${input.shortName}`;
  const title = `${name} | AACore Booking`;
  return {
    title,
    description,
    path,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      url: publicAbsoluteUrl(path),
      isPartOf: { '@id': SOFTWARE_APPLICATION_ID },
    },
  };
}

export function softwareApplicationJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': SOFTWARE_APPLICATION_ID,
    name: 'AACore Booking',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: publicAbsoluteUrl('/features'),
    provider: { '@id': 'https://www.aainomatic.com/#organization' },
  };
}

export const BLOG_INDEX_SEO = {
  title: 'Blog | AACore Booking',
  description:
    'Helpful articles for AACore Booking setup, AI booking, appointments, quotes, invoices, payments, and platform updates.',
  path: '/blog',
};

type ArticleSeoSource = {
  slug: string;
  title: string;
  summary?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  featuredImageUrl?: string | null;
  publishedAt?: string | null;
  updatedAt?: string | null;
  authorDisplayName?: string | null;
};

export function articleSeoTitle(post: Pick<ArticleSeoSource, 'title' | 'seoTitle'>): string {
  return String(post.seoTitle || '').trim() || post.title;
}

export function articleSeoDescription(
  post: Pick<ArticleSeoSource, 'summary' | 'seoDescription'>
): string {
  return String(post.seoDescription || '').trim() || String(post.summary || '').trim();
}

export function buildArticleJsonLd(post: ArticleSeoSource): Record<string, unknown> {
  const url = publicAbsoluteUrl(`/blog/${post.slug}`);
  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: articleSeoTitle(post),
    mainEntityOfPage: url,
    url,
    publisher: {
      '@id': 'https://www.aainomatic.com/#organization',
      '@type': 'Organization',
      name: 'A&A Inomatic, LLC',
    },
  };

  const description = articleSeoDescription(post);
  if (description) jsonLd.description = description;
  if (post.featuredImageUrl) jsonLd.image = post.featuredImageUrl;
  if (post.publishedAt) jsonLd.datePublished = post.publishedAt;
  if (post.updatedAt) jsonLd.dateModified = post.updatedAt;
  const author = String(post.authorDisplayName || '').trim();
  if (author) {
    jsonLd.author = { '@type': 'Person', name: author };
  }
  jsonLd.about = { '@id': SOFTWARE_APPLICATION_ID };

  return jsonLd;
}

export function buildBreadcrumbJsonLd(items: Array<{ name: string; path: string }>): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: publicAbsoluteUrl(item.path),
    })),
  };
}

export function buildBlogCollectionJsonLd(
  posts: Array<{ slug: string; title: string }>
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: BLOG_INDEX_SEO.title,
    url: publicAbsoluteUrl('/blog'),
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: posts.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: post.title,
        url: publicAbsoluteUrl(`/blog/${post.slug}`),
      })),
    },
  };
}
